// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, Link, Outlet, useMatchRoute } from "@tanstack/react-router";
import { Pencil } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useOctoRuleQuery } from "@/api/hooks/use-octo-rules";
import { PageHeader } from "@/components/layout/page-header";
import { IdPill } from "@/components/shared/id-pill";
import { KeyChip } from "@/components/shared/key-chip";
import { DetailPageSkeleton, PageProgressBar } from "@/components/shared/page-loading";
import { RequirePermission } from "@/components/shared/require-permission";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { OctoRuleActionsMenu } from "@/features/octo/octo-rule-actions-menu";
import { OctoOutcomeBadge, OctoStageBadge } from "@/features/octo/octo-rule-badges";
import { ToggleOctoRuleActiveDialog } from "@/features/octo/toggle-octo-rule-active-dialog";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";

export const Route = createFileRoute("/_authenticated/octo/rules/$ruleId")({
  validateSearch: z.object({ t: z.string().optional(), f: z.string().optional() }),
  component: () => (
    <RequirePermission permission={PAGE_PERMISSIONS.octoRules}>
      <OctoRuleDetailLayout />
    </RequirePermission>
  ),
});

/**
 * Chrome shared by the rule's read page (`/octo/rules/$ruleId`) and its edit page
 * (`/octo/rules/$ruleId/edit`): the identity pill row, the actions menu and its dialog.
 * Keeping it in the layout means the header stays mounted when moving between the two.
 */
function OctoRuleDetailLayout() {
  const { ruleId } = Route.useParams();
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const matchRoute = useMatchRoute();
  const { data: rule, isLoading, isError } = useOctoRuleQuery(Number(ruleId));

  // The button that opens the edit page is hidden once we're there — the rule-name breadcrumb
  // is the way back.
  const isEditPage = !!matchRoute({ to: "/octo/rules/$ruleId/edit" });
  const canEdit = hasPermission(PAGE_PERMISSIONS.octoRulesEdit);

  const displayName = rule ? rule.name || t("octoRules.unnamed", { id: rule.octoRuleId }) : `#${ruleId}`;

  const breadcrumbs = useMemo(
    () =>
      isEditPage
        ? [
            { label: t("octoRules.title"), to: "/octo/rules" },
            { label: displayName, to: `/octo/rules/${ruleId}`, bold: true },
          ]
        : [{ label: t("octoRules.title"), to: "/octo/rules" }],
    [t, isEditPage, displayName, ruleId],
  );

  const [toggleOpen, setToggleOpen] = useState(false);

  if (isLoading) {
    return (
      <div>
        <PageProgressBar />
        <DetailPageSkeleton />
      </div>
    );
  }

  if (isError || !rule) {
    return (
      <div>
        <PageHeader title={`#${ruleId}`} breadcrumbs={[{ label: t("octoRules.title"), to: "/octo/rules" }]} />
        <div className="p-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {t("errors.loadFailed")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={isEditPage ? t("buttons.edit") : displayName}
        subtitle={
          <>
            <IdPill id={rule.octoRuleId} />
            {rule.storeName ? (
              <KeyChip label={rule.storeName} variant="store" />
            ) : rule.chainName ? (
              <KeyChip label={rule.chainName} variant="chain" />
            ) : (
              <Badge variant="outline" className="font-mono font-normal lowercase">
                {t("octoRules.scopeGlobal")}
              </Badge>
            )}
            <OctoStageBadge stage={rule.stage} />
            <OctoOutcomeBadge outcome={rule.outcome} />
            <StatusIndicator active={rule.isActive} />
            <div className="ml-auto -my-1.5 flex items-center gap-2">
              {/* 30px rather than the `sm` 32px — sits a touch tighter in the pill row */}
              {canEdit && !isEditPage && (
                <Button asChild size="sm" className="h-7.5">
                  <Link to="/octo/rules/$ruleId/edit" params={{ ruleId }} search={{}}>
                    <Pencil className="h-4 w-4" />
                    {t("buttons.edit")}
                  </Link>
                </Button>
              )}
              <OctoRuleActionsMenu isActive={rule.isActive} onToggleActive={() => setToggleOpen(true)} />
            </div>
          </>
        }
        breadcrumbs={breadcrumbs}
      />

      <Outlet />

      <ToggleOctoRuleActiveDialog
        rule={toggleOpen ? { octoRuleId: rule.octoRuleId, name: displayName, isActive: rule.isActive } : null}
        onClose={() => setToggleOpen(false)}
      />
    </div>
  );
}
